import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CheckCircleIcon, BeakerIcon, ClipboardCheckIcon, BookOpenIcon, ArrowRightIcon, BadgeCheckIcon, ShieldCheckIcon, AwardIcon, TrendingUpIcon, FlaskConicalIcon, MicroscopeIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const CertificationPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const stepVariants = {
    hidden: {
      opacity: 0,
      x: -20
    },
    visible: {
      opacity: 1,
      x: 0
    }
  };
  return <div className="bg-white w-full" data-id="element-365" data-mp="fhmrt732xacl">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-366" data-mp="fh4ojqi01vv6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-367" data-mp="kfodlzrw3nrr">
          <div className="text-center" data-id="element-368" data-mp="3c0ymt6ctdw2z">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-369" data-mp="3bzx3yfpk45ap">
              Ukweli Certified™ Program
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-370" data-mp="3747yjvquvu6h">
              The world's first three-tier evidence-based certification for
              supplements and functional products
            </p>
          </div>
        </div>
      </section>
      {/* Introduction Section */}
      <section className="py-16 bg-white" data-id="element-371" data-mp="2t8dzs5jsql73">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-372" data-mp="2ob4d2xnsknmy">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-373" data-mp="2odqh4nbh4l0f">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-374" data-mp="hitqdx4q7npx">
              The Ukweli Certification Process
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto" data-id="element-375" data-mp="271jnaief0na4">
              Our certification process is designed to move a product from basic
              screening through to clinical-grade validation, giving both
              companies and consumers confidence in safety and efficacy. Each
              level builds upon the previous, creating a comprehensive
              evaluation framework that stands apart in the wellness industry.
            </p>
          </motion.div>
          {/* Process Overview */}
          <div className="mt-12 mb-20" data-id="element-376" data-mp="2uql1ynducunu">
            <div className="relative" data-id="element-377" data-mp="2upjm85lvrjzs">
              {/* Process Timeline */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-100" data-id="element-378" data-mp="2grbineuc9kfn"></div>
              {/* Step 1 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5
            }} className="relative mb-16" data-id="element-379" data-mp="15cy3qzhkcfbr">
                <div className="flex flex-col md:flex-row items-center" data-id="element-380" data-mp="wvflt3kl4xj8">
                  <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0" data-id="element-381" data-mp="1brn3nx8itmvc">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-382" data-mp="16u4uq6ui77nc">
                      <div className="flex items-center mb-4" data-id="element-383" data-mp="16s0rlr4fico4">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-384" data-mp="j4slj6z6ezl1">
                          <ClipboardCheckIcon size={24} className="text-blue-600" data-id="element-385" data-mp="e5z9lizjfgmz" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-386" data-mp="o33ttd9tp215">
                          Step 1: Application & Intake
                        </h3>
                      </div>
                      <ul className="space-y-3 text-gray-700" data-id="element-387" data-mp="3rkm2fmxf6d2m">
                        <li className="flex items-start" data-id="element-388" data-mp="3rhzuhm6m9vjv">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-389" data-mp="2utqmoyc5iqk3" />
                          <span data-id="element-390" data-mp="33t5bzugl0v30">
                            Supplement, probiotic, or nutraceutical companies
                            apply to have their product evaluated.
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-391" data-mp="3dixav0vpsada">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-392" data-mp="3ihid15uj144d" />
                          <span data-id="element-393" data-mp="3rjhtnx1mq66s">
                            The Institute reviews product dossiers (ingredients,
                            claims, manufacturing details).
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-394" data-mp="1x5qcmcgz0k1b">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-395" data-mp="1pl610nuo3ln9" />
                          <span data-id="element-396" data-mp="1fvmftbjhffp8">
                            A certification pathway (Level 1, 2, or 3) is
                            selected depending on the company's needs and
                            evidence available.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-397" data-mp="23m09f6hptmlx">
                    <span className="text-lg font-bold" data-id="element-398" data-mp="1uksnlnwdye8q">1</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12" data-id="element-399" data-mp="1umveea5d4bei">
                    <img src="https://images.unsplash.com/photo-1579621970795-87facc2f976d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" alt="Application process" className="rounded-lg shadow-md w-full h-auto" data-id="element-400" data-mp="n7qji7xtrpsz" />
                  </div>
                </div>
              </motion.div>
              {/* Step 2 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="relative mb-16" data-id="element-401" data-mp="anjv2azlvd6n">
                <div className="flex flex-col md:flex-row-reverse items-center" data-id="element-402" data-mp="3o4asz074heo5">
                  <div className="md:w-1/2 md:pl-12 mb-8 md:mb-0" data-id="element-403" data-mp="3t1t48x8m45zb">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-404" data-mp="3szztp6uvp1m1">
                      <div className="flex items-center mb-4" data-id="element-405" data-mp="3f3cxj6ar2fh6">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-406" data-mp="3j4ensxmovh70">
                          <ShieldCheckIcon size={24} className="text-blue-600" data-id="element-407" data-mp="30fkwm2ljuhgm" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-408" data-mp="39gj4wx30tb50">
                          Step 2: Level 1 – Evidence Screen
                        </h3>
                      </div>
                      <p className="text-blue-600 font-semibold mb-3" data-id="element-409" data-mp="34jsmi33qu3gf">
                        Cost: $7,000
                      </p>
                      <div className="mb-4" data-id="element-410" data-mp="3egf9oyjjl6lm">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-411" data-mp="3edjaz7zvedw4">
                          Tests conducted:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-412" data-mp="3o56xcuhkl4qq">
                          <li className="flex items-start" data-id="element-413" data-mp="5ztwf5jvnrcp">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-414" data-mp="60bzal11p3tl" />
                            <span data-id="element-415" data-mp="18cn635ijeipe">Purity & safety compliance.</span>
                          </li>
                          <li className="flex items-start" data-id="element-416" data-mp="1cdgxi8v6ufn1">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-417" data-mp="1hdcz61gexuzf" />
                            <span data-id="element-418" data-mp="oph574i8ah6c">
                              Verification of label claims (dosage accuracy,
                              absence of contaminants or adulterants).
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div data-id="element-419" data-mp="13elnelp0wleg">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-420" data-mp="13evfqfxjj4nb">
                          Outcome:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-421" data-mp="5wva4mf5aumt">
                          <li className="flex items-start" data-id="element-422" data-mp="aw803g1bchtm">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-423" data-mp="3etagxsce572" />
                            <span data-id="element-424" data-mp="3pkyl1h2tjphw">
                              If compliant, product earns Ukweli Certified™ –
                              Level 1.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-425" data-mp="3glv1yd3nja3e">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-426" data-mp="3bu391vx0wsa3" />
                            <span data-id="element-427" data-mp="36snr4cq571vx">
                              Report is published in an accessible, transparent
                              format for consumers and professionals.
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-428" data-mp="2xqq9bgf1hhdj">
                    <span className="text-lg font-bold" data-id="element-429" data-mp="2sr3ux3nh5v35">2</span>
                  </div>
                  <div className="md:w-1/2 md:pr-12" data-id="element-430" data-mp="37jxb6rakmf8v">
                    <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200" data-id="element-431" data-mp="32iht983owoup">
                      <div className="flex justify-between items-start mb-6" data-id="element-432" data-mp="2esdouyiogl2n">
                        <div className="flex items-center" data-id="element-433" data-mp="29uc5sdl94lr6">
                          <BadgeCheckIcon size={32} className="text-blue-600 mr-3" data-id="element-434" data-mp="29vwydxo09ume" />
                          <h4 className="text-xl font-bold text-gray-900" data-id="element-435" data-mp="2jr70smibg40p">
                            Level 1 Certification
                          </h4>
                        </div>
                        <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-436" data-mp="3v86sbfwuaeto">
                          Basic Verification
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-437" data-mp="blobyavhd2wg">
                        <img src="https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Level 1 Certification" className="w-full h-40 object-cover rounded-md mb-4" data-id="element-438" data-mp="biacpbi459t8" />
                        <div className="flex justify-center" data-id="element-439" data-mp="ubnzuufl4bya">
                          <div className="bg-blue-100 rounded-full p-3" data-id="element-440" data-mp="ud7dsedrqaww">
                            <ShieldCheckIcon size={40} className="text-blue-600" data-id="element-441" data-mp="flp3mx7lbalg" />
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-center" data-id="element-442" data-mp="1d25p2p17kpew">
                        Verifies basic safety and label accuracy, ensuring the
                        product contains what it claims and is free from harmful
                        contaminants.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* Step 3 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="relative mb-16" data-id="element-443" data-mp="18ymn0d7esk77">
                <div className="flex flex-col md:flex-row items-center" data-id="element-444" data-mp="q7vjvte0lq19">
                  <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0" data-id="element-445" data-mp="q5iskpmda3bj">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-446" data-mp="l7poc24mjjdw">
                      <div className="flex items-center mb-4" data-id="element-447" data-mp="ga7bw1reem15">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-448" data-mp="2rwhv6xn8dtku">
                          <FlaskConicalIcon size={24} className="text-blue-600" data-id="element-449" data-mp="2rylvz6pu2cgw" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-450" data-mp="31vgxzzlbeuzx">
                          Step 3: Level 2 – Mechanistic Validation
                        </h3>
                      </div>
                      <p className="text-blue-600 font-semibold mb-3" data-id="element-451" data-mp="297urojkpdc9d">
                        Cost: $14,000
                      </p>
                      <div className="mb-4" data-id="element-452" data-mp="2i6yd3u7ce3r1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-453" data-mp="2i5wqqtkvluu0">
                          Tests conducted:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-454" data-mp="1qfs5ytt7f5s9">
                          <li className="flex items-start" data-id="element-455" data-mp="1qdfsotye5jlj">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-456" data-mp="1ug2cq8otm086" />
                            <span data-id="element-457" data-mp="16e76o8d9k7bc">
                              In vitro lab assays (e.g., biofilm disruption,
                              cell culture immune response).
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-458" data-mp="1gaadvsmz2w55">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-459" data-mp="1u1yry1gs7uor" />
                            <span data-id="element-460" data-mp="241fwugo7m4jo">
                              Biomarker testing (PKR pathway, cytokine
                              modulation, metabolic activity).
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-461" data-mp="9neyry0hhlkn">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-462" data-mp="4otxdv9hwvum" />
                            <span data-id="element-463" data-mp="2lb0ci33vgois">
                              Evidence that the compound produces biologically
                              plausible effects.
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div data-id="element-464" data-mp="1xl5yhi7tgtwy">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-465" data-mp="1xjuubhaglk5b">
                          Outcome:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-466" data-mp="21k6depn6bceo">
                          <li className="flex items-start" data-id="element-467" data-mp="1no1iy18h7wp4">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-468" data-mp="1sll29fk16znx" />
                            <span data-id="element-469" data-mp="3mzlv9jswgsg3">
                              If confirmed, product earns Ukweli Certified™ –
                              Level 2.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-470" data-mp="3gjc9rlxlvcas">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-471" data-mp="3glyfrh4uljqw" />
                            <span data-id="element-472" data-mp="36nsgl7aferpq">
                              Certificate highlights mechanistic evidence and
                              product's scientific backing.
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-473" data-mp="3pl21ytzbaf9m">
                    <span className="text-lg font-bold" data-id="element-474" data-mp="qvl421t8imw2">3</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12" data-id="element-475" data-mp="m20e8x1soz7y">
                    <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200" data-id="element-476" data-mp="uchafeedp1s1">
                      <div className="flex justify-between items-start mb-6" data-id="element-477" data-mp="ydj7ppoqj3rd">
                        <div className="flex items-center" data-id="element-478" data-mp="yfm7v2jlqd9t">
                          <AwardIcon size={32} className="text-blue-600 mr-3" data-id="element-479" data-mp="13c4c81b0s9lm" />
                          <h4 className="text-xl font-bold text-gray-900" data-id="element-480" data-mp="18c8pligmso7m">
                            Level 2 Certification
                          </h4>
                        </div>
                        <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-481" data-mp="1hc5975o5ywvt">
                          Efficacy Evidence
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-482" data-mp="1r2q9qyneebhd">
                        <img src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Level 2 Certification" className="w-full h-40 object-cover rounded-md mb-4" data-id="element-483" data-mp="1r4kqe0rmzhfn" />
                        <div className="flex justify-center" data-id="element-484" data-mp="2hcrui6enss0u">
                          <div className="bg-blue-100 rounded-full p-3" data-id="element-485" data-mp="2hc89uhxmjpj4">
                            <MicroscopeIcon size={40} className="text-blue-600" data-id="element-486" data-mp="2vazpw9w1rl9w" />
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-center" data-id="element-487" data-mp="1yma5ocwy6gkr">
                        Confirms that the product demonstrates measurable
                        biological activity in laboratory testing, validating
                        its mechanism of action.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* Step 4 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="relative mb-16" data-id="element-488" data-mp="1eykiyk3vb6ie">
                <div className="flex flex-col md:flex-row-reverse items-center" data-id="element-489" data-mp="3mjjc99cwzqtd">
                  <div className="md:w-1/2 md:pl-12 mb-8 md:mb-0" data-id="element-490" data-mp="3mhf2iasc3xl6">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-491" data-mp="e4hgfaan6snt">
                      <div className="flex items-center mb-4" data-id="element-492" data-mp="i4jj9ms45s4o">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-493" data-mp="45s6c3ozmci4">
                          <div size={24} className="text-blue-600" data-id="element-494" data-mp="47wa8lmvzbie" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-495" data-mp="96zfo76s3jqw">
                          Step 4: Level 3 – Clinical Validation
                        </h3>
                      </div>
                      <p className="text-blue-600 font-semibold mb-3" data-id="element-496" data-mp="2tt107iu7qf3t">
                        Cost: $28,000
                      </p>
                      <div className="mb-4" data-id="element-497" data-mp="393cbeo9uq1fa">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-498" data-mp="3932l0zkw9s92">
                          Tests conducted:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-499" data-mp="2z550on65oflq">
                          <li className="flex items-start" data-id="element-500" data-mp="2z7hnbdmuze54">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-501" data-mp="2lcyrxm5c1awb" />
                            <span data-id="element-502" data-mp="2bgvhzu48o7yt">
                              Human clinical trial data reviewed by an
                              independent scientific panel.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-503" data-mp="22fxkzvs7x0pq">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-504" data-mp="27eid17xbge33" />
                            <span data-id="element-505" data-mp="1iu5j6h9devf2">
                              Randomized pilot or larger-scale trials assessing
                              tolerability, efficacy, and outcomes.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-506" data-mp="1stmm4qx8mv7c">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-507" data-mp="26q9lf0cnxxgf" />
                            <span data-id="element-508" data-mp="2gg2v1vt8w2i1">
                              Measures include microbiome diversity, barrier
                              integrity, inflammation markers, and
                              patient-reported results.
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div data-id="element-509" data-mp="2kgv5uthrmfgf">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-510" data-mp="1i5tht2a2vhq0">
                          Outcome:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-511" data-mp="1n4mnw9n1c6g5">
                          <li className="flex items-start" data-id="element-512" data-mp="1r4x40f4jkd8q">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-513" data-mp="1vwojnum5syk3" />
                            <span data-id="element-514" data-mp="20y5n9xw12z4d">
                              If validated, product earns Ukweli Certified™ –
                              Level 3 — the highest seal of credibility.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-515" data-mp="2eu9ah78hi77c">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-516" data-mp="gg7ma6oarolf" />
                            <span data-id="element-517" data-mp="cy0hzawe2w69">
                              Report is peer-reviewed style and publicly
                              accessible.
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-518" data-mp="3vcjdnar8fdzv">
                    <span className="text-lg font-bold" data-id="element-519" data-mp="vwcsazz1ec3f">4</span>
                  </div>
                  <div className="md:w-1/2 md:pr-12" data-id="element-520" data-mp="qz32vkbne0fl">
                    <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200" data-id="element-521" data-mp="mvy15jze62s0">
                      <div className="flex justify-between items-start mb-6" data-id="element-522" data-mp="hx3iqj5c6g8f">
                        <div className="flex items-center" data-id="element-523" data-mp="hz7qjc6cvzdz">
                          <TrendingUpIcon size={32} className="text-blue-600 mr-3" data-id="element-524" data-mp="37n6x1ptks3nj" />
                          <h4 className="text-xl font-bold text-gray-900" data-id="element-525" data-mp="uew5kghnp27w">
                            Level 3 Certification
                          </h4>
                        </div>
                        <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-526" data-mp="ldy4o72ilatj">
                          Clinical Validation
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-527" data-mp="1d4m33bct023i">
                        <img src="https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Level 3 Certification" className="w-full h-40 object-cover rounded-md mb-4" data-id="element-528" data-mp="1d5nhnpt136pz" />
                        <div className="flex justify-center" data-id="element-529" data-mp="195d67xmgvo3q">
                          <div className="bg-blue-100 rounded-full p-3" data-id="element-530" data-mp="3ot3iv4362j12">
                            <UsersIcon size={40} className="text-blue-600" data-id="element-531" data-mp="3tu1983ybyjdn" />
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-center" data-id="element-532" data-mp="3hb5oy7xgxgiy">
                        Provides the highest level of validation through human
                        clinical trials, demonstrating real-world effectiveness
                        and safety.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* Step 5 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="relative" data-id="element-533" data-mp="2xng30hc7qahn">
                <div className="flex flex-col md:flex-row items-center" data-id="element-534" data-mp="2jtf0rdm1y5cj">
                  <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0" data-id="element-535" data-mp="2ew5ci1aefzqa">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-536" data-mp="iubrkt84d5b2">
                      <div className="flex items-center mb-4" data-id="element-537" data-mp="nsdane5jp4mj">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-538" data-mp="nqsczfnxp5kg">
                          <BookOpenIcon size={24} className="text-blue-600" data-id="element-539" data-mp="skmvwgvptgih" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-540" data-mp="wkfaba7sigvr">
                          Step 5: Publication & Consumer Access
                        </h3>
                      </div>
                      <ul className="space-y-3 text-gray-700" data-id="element-541" data-mp="4ukaukq7xatd">
                        <li className="flex items-start" data-id="element-542" data-mp="8uun2f4lsxgo">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-543" data-mp="8xgwyleyvp22" />
                          <span data-id="element-544" data-mp="1ka9l7m8jtvdd">
                            Results from all three levels are summarized in
                            open-access reports.
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-545" data-mp="1zfdoyseynd76">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-546" data-mp="1ufrbqiz4twyd" />
                          <span data-id="element-547" data-mp="1ll36gstk4f9k">
                            Certified products can display the Ukweli
                            Certified™ seal on packaging and marketing.
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-548" data-mp="1bmneknmph1z0">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-549" data-mp="16o3tepfxru7p" />
                          <span data-id="element-550" data-mp="313eodtys3fbs">
                            Consumers, clinicians, and regulators can reference
                            the certification database to identify trusted,
                            evidence-based products.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-551" data-mp="36k9jo9114ilx">
                    <span className="text-lg font-bold" data-id="element-552" data-mp="3bib2csujmfwv">5</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12" data-id="element-553" data-mp="2wlso0yr5bftj">
                    <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" alt="Publication and consumer access" className="rounded-lg shadow-md w-full h-auto" data-id="element-554" data-mp="2wo6esvesm5va" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* Why This Matters Section */}
      <section className="py-16 bg-gray-50" data-id="element-555" data-mp="qsc8fnac27d9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-556" data-mp="10wsjm3cvb0aq">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-557" data-mp="10uy1ewszdmac">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-558" data-mp="1jlp5pjy42mhv">
              <span className="inline-block bg-blue-600 text-white rounded-full h-10 w-10 flex items-center justify-center mr-3" data-id="element-559" data-mp="1endvh83wm9z4">
                🎯
              </span>
              Why This Matters
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-560" data-mp="1onxyogazhojz">
              The Ukweli Certification process creates value across the entire
              wellness ecosystem
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-561" data-mp="3qfswpt4bhujq">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white p-8 rounded-lg shadow-md" data-id="element-562" data-mp="2zb5aapv8de4y">
              <div className="flex items-center mb-4" data-id="element-563" data-mp="2bmm4cgznldg1">
                <div className="bg-blue-100 p-3 rounded-full mr-3" data-id="element-564" data-mp="2bl188mxoxmg2">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-565" data-mp="26mpyec7ebbxu">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" data-id="element-566" data-mp="3w2fut7ezcqpq" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-567" data-mp="h8gsf0g80bxu">
                  For Companies
                </h3>
              </div>
              <p className="text-gray-700" data-id="element-568" data-mp="3ll3uhkdacpxc">
                Certification provides a competitive edge and consumer trust. In
                a crowded marketplace, the Ukweli Certified™ seal
                differentiates products backed by real science, building brand
                credibility and opening doors to discerning consumers and
                healthcare providers.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white p-8 rounded-lg shadow-md" data-id="element-569" data-mp="19u9oysaxauc6">
              <div className="flex items-center mb-4" data-id="element-570" data-mp="u5d1gi43aii4">
                <div className="bg-blue-100 p-3 rounded-full mr-3" data-id="element-571" data-mp="u5v4pyp65wzj">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-572" data-mp="6gkdp8xmfgyl">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-573" data-mp="bevmrhg3dnfr" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-574" data-mp="1kfdo3nzperqu">
                  For Consumers
                </h3>
              </div>
              <p className="text-gray-700" data-id="element-575" data-mp="1peys4ctqdqaf">
                The seal ensures truth you can trust in a crowded wellness
                market. Consumers can make informed decisions based on
                transparent, independent testing rather than marketing claims,
                leading to better health outcomes and more effective use of
                wellness products.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white p-8 rounded-lg shadow-md" data-id="element-576" data-mp="286rkc50z77h3">
              <div className="flex items-center mb-4" data-id="element-577" data-mp="137w9opncz3i3">
                <div className="bg-blue-100 p-3 rounded-full mr-3" data-id="element-578" data-mp="y7rt6zmga8rv">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-579" data-mp="y9l10i8suz1g">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" data-id="element-580" data-mp="1lwkpwyawcknj" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-581" data-mp="2i5s60r7dnl4x">For Science</h3>
              </div>
              <p className="text-gray-700" data-id="element-582" data-mp="2m5sja8iqpqae">
                Each certification contributes to the global body of
                transparent, reproducible evidence. By creating standardized
                testing protocols and open-access data, the Ukweli Institute
                elevates the entire field of wellness science and creates a
                foundation for future research.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-white" data-id="element-583" data-mp="39vwq0or8665m">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-584" data-mp="3euq0s9f4nj23">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-50 rounded-xl p-8 md:p-12 text-center" data-id="element-585" data-mp="3eru4epixh2fr">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-586" data-mp="2wrrdzf02wy2y">
              Ready to Get Certified?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8" data-id="element-587" data-mp="2rs6acr9ys1jw">
              Join the growing community of evidence-based wellness products
              that stand apart with the Ukweli Certified™ seal. Our team is
              ready to guide you through the certification process.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-588" data-mp="3fiileb0iaeep">
              <Link to="/contact" className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center" data-id="element-589" data-mp="3bgx6kk4evq1v">
                Apply for Certification{' '}
                <ArrowRightIcon size={16} className="ml-2" data-id="element-590" data-mp="7xrc099lurqj" />
              </Link>
              <Link to="/research" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-gray-100 transition-colors duration-300" data-id="element-591" data-mp="7vohbbxicafh">
                Learn About Our Research
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};